/**
 * Local-intent landing pages — 4 products × 3 cities = 12 unique pages.
 *
 * Each combination has a unique URL like /loan/personal-loan-vyara so it
 * matches the way local customers actually search ("personal loan in vyara",
 * "agriculture loan tapi", etc.).
 *
 * The data is shaped so the LocalLandingPage component can render the same
 * layout but feel custom for every city+product combination.
 */

export const CITIES = {
  vyara: {
    slug: "vyara",
    name: "Vyara",
    full: "Vyara, Tapi District",
    pincode: "394650",
    region: "South Gujarat",
    landmark: "A-106, Ambaji Complex, Vyara",
    populationLine: "Tribal-majority hub with ~50,000 residents",
    economyLine: "Largely agricultural with a growing trader & MSME base",
    nearby: ["Songadh", "Vyara Rural", "Ucchhal", "Nizar", "Dolvan"],
    landmarks: ["Vyara Railway Station", "Government Hospital", "MTB College"],
    branchLine: "Walk-in counter open 10 AM – 6 PM (Mon–Sat) at our Ambaji Complex office",
    primaryLanguage: "Gujarati",
    inboundCta: "Visit our Vyara branch or WhatsApp 70169 75324",
  },
  tapi: {
    slug: "tapi",
    name: "Tapi",
    full: "Tapi District, Gujarat",
    pincode: "394650",
    region: "South Gujarat",
    landmark: "Sahyog Finance HQ · Ambaji Complex, Vyara (district HQ)",
    populationLine: "District of ~8.1 lakh residents across 524 villages",
    economyLine: "Predominantly agri economy (sugarcane, paddy, banana) with diaspora remittances",
    nearby: ["Vyara", "Songadh", "Ucchhal", "Dolvan", "Nizar", "Valod", "Kukarmunda"],
    landmarks: ["Ukai Dam", "Songadh Fort", "Vyara Taluka Panchayat"],
    branchLine: "Single point of contact for all 7 talukas — Sahyog HQ at Vyara",
    primaryLanguage: "Gujarati",
    inboundCta: "Talk to the district team on +91 70169 75324",
  },
  surat: {
    slug: "surat",
    name: "Surat",
    full: "Surat, Gujarat",
    pincode: "395003",
    region: "South Gujarat",
    landmark: "Sahyog DSA network across Adajan, Vesu, Varachha, Katargam",
    populationLine: "8th-largest metro in India with ~70 lakh residents",
    economyLine: "Diamond polishing, textile, embroidery & rapidly growing IT/MSME",
    nearby: ["Adajan", "Vesu", "Varachha", "Katargam", "Olpad", "Bardoli", "Kamrej"],
    landmarks: ["Tapi Riverfront", "Dumas Beach", "VR Mall Surat"],
    branchLine: "Door-step service via our DSA partner network across Surat",
    primaryLanguage: "Gujarati / Hindi",
    inboundCta: "Call your nearest Surat DSA on +91 70169 75324",
  },
};

export const PRODUCTS = {
  "personal-loan": {
    slug: "personal-loan",
    name: "Personal Loan",
    icon: "Wallet",
    accent: "#D4AF37",
    roi: "1.25% per month onwards",
    tenure: "Up to 60 months",
    minTicket: "₹50,000",
    maxTicket: "₹25,00,000",
    processingFee: "1.0% – 2.5% of loan amount",
    tagline: "Get cash for weddings, medical, education or any genuine need.",
    useCases: [
      "Wedding & functions",
      "Medical emergency",
      "Higher education abroad",
      "Home renovation",
      "Debt consolidation",
      "Family vacation",
    ],
    docs: ["KYC (Aadhaar + PAN)", "Salary slip (last 3 months) / IT returns", "6-month bank statement", "Address proof"],
    eligibility: [
      "Age 21–65 years",
      "Salaried with min ₹15,000/mo income OR self-employed with 2-yr ITR",
      "Indian citizen with Aadhaar",
    ],
    faqs: [
      ["What if I don't have a strong CIBIL?", "Sahyog Finance has special programs for low-CIBIL borrowers — your latest bank statement and income stability are what we focus on."],
      ["How quickly can I get the money?", "For complete files, typical disbursal happens in 48–72 working hours."],
      ["Can I prepay without penalty?", "Yes — partial or full prepayment is allowed after 6 EMIs with zero foreclosure charges."],
    ],
    benefit: "Money in your account in as little as 48 hours.",
  },
  "business-loan": {
    slug: "business-loan",
    name: "Business Loan",
    icon: "Briefcase",
    accent: "#F0D788",
    roi: "1.40% per month onwards",
    tenure: "Up to 84 months",
    minTicket: "₹1,00,000",
    maxTicket: "₹50,00,000",
    processingFee: "1.5% – 2.5% of sanctioned amount",
    tagline: "Working capital, equipment purchase or expansion — without losing equity.",
    useCases: [
      "Working capital boost",
      "Machinery & equipment purchase",
      "Inventory build-up before festive season",
      "New branch / outlet",
      "Salary & vendor payments",
      "Marketing & digital spend",
    ],
    docs: ["GST registration / Udyam", "Last 2 yrs ITR + financials", "12-month current-account statement", "KYC of proprietor / partners"],
    eligibility: [
      "Business in operation for at least 24 months",
      "Annual turnover ≥ ₹15 lakh",
      "Owner age 25–65",
    ],
    faqs: [
      ["Is collateral required?", "Loans up to ₹10 lakh are typically unsecured. Higher tickets may need property, inventory or receivables as security."],
      ["Do you fund new businesses?", "We currently fund businesses with at least 2 years of operating history. Startups can apply via our Project Loan vertical."],
      ["What's the fastest sanction time?", "Pre-approved cases get a same-day soft sanction; full disbursal in 5–7 working days."],
    ],
    benefit: "Scale your business without giving away equity.",
  },
  "agriculture-loan": {
    slug: "agriculture-loan",
    name: "Agriculture Loan",
    icon: "Sprout",
    accent: "#7CB342",
    roi: "1.10% per month onwards",
    tenure: "Crop loan (12 mo) or Term loan (up to 7 yrs)",
    minTicket: "₹25,000",
    maxTicket: "₹20,00,000",
    processingFee: "Up to 1.0% (waived for KCC borrowers)",
    tagline: "Crop, dairy, pashupalan, drip irrigation, tractor — funded the right way.",
    useCases: [
      "Seasonal crop input (seed, fertilizer, pesticide)",
      "Drip & sprinkler irrigation",
      "Tractor / harvester purchase",
      "Pashupalan (dairy cattle, goat-rearing)",
      "Poly-house / shade-net farming",
      "Warehouse & cold-storage",
    ],
    docs: ["7/12 utara + 8-A", "KYC + Aadhaar", "Sarpanch / village authority letter (optional)", "Bank passbook copy"],
    eligibility: [
      "Owner or tenant farmer with cultivable land",
      "Age 21–70",
      "Cooperative / FPO membership preferred (faster processing)",
    ],
    faqs: [
      ["Do I need land in my own name?", "Owned, leased or share-cropped — all are accepted with the right documents."],
      ["Can I get a Pashupalan (cattle) loan separately?", "Yes — we have a dedicated Cattle Loan product up to ₹5 lakh per buffalo/cow."],
      ["What if my crop fails?", "We offer EMI restructuring options for declared natural-calamity-affected districts."],
    ],
    benefit: "Sow the season with cash in hand — repay after harvest.",
  },
  "housing-loan": {
    slug: "housing-loan",
    name: "Housing Loan",
    icon: "Home",
    accent: "#5DADE2",
    roi: "0.95% per month onwards",
    tenure: "Up to 30 years",
    minTicket: "₹3,00,000",
    maxTicket: "₹2,00,00,000",
    processingFee: "0.50% – 1.0% (subject to amount)",
    tagline: "Buy your dream home, build on your plot, or refinance an existing loan.",
    useCases: [
      "Purchase of ready or under-construction home",
      "Construction on owned plot",
      "Home extension / floor addition",
      "Balance-transfer from another bank",
      "Plot + construction composite loan",
      "Home improvement & interior",
    ],
    docs: ["KYC + Aadhaar + PAN", "Last 6-month bank statement", "Salary slip / ITR", "Property title chain + 7/12 utara", "Approved layout & sanctioned plan"],
    eligibility: [
      "Age 23–70 (loan must close by 70)",
      "Min net monthly income ₹20,000",
      "Property within Sahyog operating circle (currently South Gujarat + Surat)",
    ],
    faqs: [
      ["What's the maximum LTV?", "Up to 90% of property value for tickets up to ₹30 lakh; 80% for ₹30–75 lakh; 75% above."],
      ["Can I switch from my existing bank?", "Yes — our balance-transfer scheme often reduces your EMI by ₹500–₹2,000 per lakh."],
      ["Are PMAY subsidies available?", "Eligible applicants under EWS/LIG/MIG can avail PMAY-CLSS subsidy through us as a partner channel."],
    ],
    benefit: "Own your home — pay less than rent, EMIs as low as ₹650 per lakh.",
  },
};

// Active combinations (the 12 pages)
export const COMBINATIONS = [
  ["personal-loan", "vyara"],
  ["personal-loan", "tapi"],
  ["personal-loan", "surat"],
  ["business-loan", "vyara"],
  ["business-loan", "tapi"],
  ["business-loan", "surat"],
  ["agriculture-loan", "vyara"],
  ["agriculture-loan", "tapi"],
  ["agriculture-loan", "surat"],
  ["housing-loan", "vyara"],
  ["housing-loan", "tapi"],
  ["housing-loan", "surat"],
];

export const buildCombo = (productSlug, citySlug) => {
  const product = PRODUCTS[productSlug];
  const city = CITIES[citySlug];
  if (!product || !city) return null;
  return {
    product,
    city,
    slug: `${productSlug}-${citySlug}`,
    title: `${product.name} in ${city.name}`,
    seoTitle: `${product.name} in ${city.name} · ${product.roi}`,
    seoDescription: `${product.name} for residents of ${city.full}. ${product.tagline} ROI from ${product.roi}, ticket size ${product.minTicket} to ${product.maxTicket}. No CIBIL? Special programs available.`,
    path: `/loan/${productSlug}-${citySlug}`,
  };
};

export const ALL_COMBOS = COMBINATIONS.map(([p, c]) => buildCombo(p, c)).filter(Boolean);
